import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import WritingHandwritingPad from "../WritingHandwritingPad";
import WritingFormationDemo from "../WritingFormationDemo";
import { WRITING_ASSET } from "../writingAssets";
import { writingLocalStore } from "../writingLocalStore";
import {
  WRITING_KNOWN_LETTERS,
  lessonLetters,
  lessonMysteryPrompt,
  lessonSentences,
  watchAndTapChoices,
  whichIsRightChoices,
  writingLetterNarration,
} from "../../../../data/writingCurriculum";
import {
  Board,
  ChoiceButton,
  ChoiceRow,
  CollectedStrip,
  GameFrame,
  IconButton,
  PlayIcon,
  RepeatDots,
  RoundBar,
  RoundCounter,
  ScorePill,
  SentenceFrame,
  SpeakerIcon,
  TargetChip,
  TimerPill,
  WordPicture,
  useCompleteOnce,
  useCountdown,
  useSpokenItem,
  useWrongFlash,
} from "./shared";

const useRoundLetters = (lessonNumber) => lessonLetters(lessonNumber) || WRITING_KNOWN_LETTERS[2];

const PaintingPip = ({ size = "calc(var(--tile, 72px) * 1.1)" }) => (
  <img
    src={WRITING_ASSET("paint_panda")}
    alt=""
    aria-hidden="true"
    draggable={false}
    onError={(e) => { e.currentTarget.style.display = "none"; }}
    style={{ position: "absolute", left: 8, bottom: 6, width: size, height: size, objectFit: "contain", pointerEvents: "none", filter: "drop-shadow(0 6px 10px rgba(11,26,51,0.18))" }}
  />
);

/* ── Watch and Tap ──────────────────────────────────────────────────────── */
export const WatchAndTapGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = useRoundLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [take, setTake] = useState(0);              // bump to replay the demo
  const [choicesVisible, setChoicesVisible] = useState(false);
  const [picked, setPicked] = useState(null);
  const { flash, isWrong } = useWrongFlash();
  const letter = letters[index];
  const choices = useMemo(() => watchAndTapChoices(letters, letter, index), [letters, letter, index]);

  useEffect(() => {
    setChoicesVisible(false);
    setPicked(null);
  }, [index, take]);

  const demoFinished = useCallback(() => {
    setChoicesVisible(true);
    // Under reduced motion the demo ends before the player's intro starts speaking.
    window.setTimeout(() => speak?.(writingLetterNarration(letter), { dedupe: true }), reduceMotion ? 800 : 0);
  }, [letter, reduceMotion, speak]);

  const pick = (choice) => {
    if (!choicesVisible || picked) return;
    if (choice !== letter) {
      flash(choice);
      onWrong?.();
      speak?.("Not that one. Look closely and try again.", { interrupt: true });
      return;
    }
    sfx?.("correct");
    setPicked(choice);
    speak?.(`Yes. ${writingLetterNarration(letter)}`, { interrupt: true });
    window.setTimeout(() => {
      if (index + 1 >= letters.length) finish();
      else setIndex((n) => n + 1);
    }, reduceMotion ? 200 : 900);
  };

  return (
    <GameFrame>
      <RoundBar
        left={<RoundCounter done={index} total={letters.length} accent={activity.accent} />}
        right={
          <IconButton label="Watch Pip draw it again" onClick={() => { sfx?.("pop", { volume: 0.4 }); setTake((t) => t + 1); }} accent={activity.accent}>
            <PlayIcon size={20} />
          </IconButton>
        }
      />
      <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
        <WritingFormationDemo key={`${letter}-${take}`} letter={letter} accent={activity.accent} reduceMotion={reduceMotion} onFinished={demoFinished} />
        {activity.paintPanda && <PaintingPip />}
      </div>
      <ChoiceRow style={{ minHeight: "calc(var(--tile, 72px) + 8px)" }}>
        {choices.map((choice, i) => (
          <motion.div
            key={`${letter}-${take}-${choice}`}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.7, y: 12 }}
            animate={choicesVisible ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0.25, scale: 0.9, y: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 22, delay: choicesVisible && !reduceMotion ? i * 0.07 : 0 }}
          >
            <ChoiceButton
              label={choice}
              ariaLabel={`Lowercase ${choice}`}
              accent={activity.accent}
              onClick={() => pick(choice)}
              disabled={!choicesVisible || !!picked}
              wrong={isWrong(choice)}
              correct={picked === choice}
              dim={!!picked && picked !== choice}
              reduceMotion={reduceMotion}
            />
          </motion.div>
        ))}
      </ChoiceRow>
    </GameFrame>
  );
};

/* ── Guided Trace ───────────────────────────────────────────────────────── */
export const GuidedTraceGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = useRoundLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const needed = activity.tracesPerLetter || 2;
  const [index, setIndex] = useState(0);
  const [hits, setHits] = useState(0);
  const letter = letters[index];

  const accepted = () => {
    const nextHits = hits + 1;
    if (nextHits < needed) {
      setHits(nextHits);
      speak?.(`Lovely. Trace lowercase ${letter} once more.`, { interrupt: true });
      return;
    }
    if (index + 1 >= letters.length) finish();
    else {
      speak?.(`Great tracing. Next is lowercase ${letters[index + 1]}.`, { interrupt: true });
      setIndex((n) => n + 1);
      setHits(0);
    }
  };

  return (
    <GameFrame>
      <RoundBar
        left={
          <TargetChip glyph={letter} caption="Trace" accent={activity.accent}>
            <RepeatDots total={needed} done={hits} accent={activity.accent} />
          </TargetChip>
        }
        right={<RoundCounter done={index} total={letters.length} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={`${letter}-${hits}`}
        target={letter}
        accent={activity.accent}
        showsGuide
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── From Memory ────────────────────────────────────────────────────────── */
export const FromMemoryGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = useRoundLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const needed = activity.writesPerLetter || 3;
  const [index, setIndex] = useState(0);
  const [hits, setHits] = useState(0);
  const letter = letters[index];

  // The item, not the prompt: the player reads the intro itself.
  useSpokenItem(speak, writingLetterNarration(letter));

  const accepted = () => {
    const nextHits = hits + 1;
    if (nextHits < needed) {
      setHits(nextHits);
      speak?.(nextHits === 1 ? `Nice work. Write another lowercase ${letter}.` : `Great job. One more lowercase ${letter}.`, { interrupt: true });
      return;
    }
    if (index + 1 >= letters.length) finish();
    else {
      setIndex((n) => n + 1);
      setHits(0);
    }
  };

  return (
    <GameFrame>
      <RoundBar
        left={
          <TargetChip glyph={letter} caption="Write" accent={activity.accent}>
            <RepeatDots total={needed} done={hits} accent={activity.accent} />
          </TargetChip>
        }
        right={
          <>
            <IconButton label="Hear the letter again" onClick={() => speak?.(writingLetterNarration(letter), { interrupt: true })} accent={activity.accent}>
              <SpeakerIcon size={20} />
            </IconButton>
            <RoundCounter done={index} total={letters.length} accent={activity.accent} />
          </>
        }
      />
      <WritingHandwritingPad
        key={`${letter}-${hits}`}
        target={letter}
        accent={activity.accent}
        showsGuide={false}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => {
          onWrong?.();
          speak?.("That shape is still growing. Fix it or clear the paper and try once more.", { interrupt: true });
        }}
      />
    </GameFrame>
  );
};

/* ── Which Is Right? ────────────────────────────────────────────────────── */
export const WhichIsRightGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = useRoundLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const rounds = activity.rounds || 5;
  const [round, setRound] = useState(0);
  const [picked, setPicked] = useState(null);
  const { flash, isWrong } = useWrongFlash();
  const target = letters[round % letters.length];
  const choices = useMemo(() => whichIsRightChoices(letters, target, round), [letters, target, round]);
  const flipped = choices.find((c) => c !== target) || choices[0];

  const pick = (choice) => {
    if (picked) return;
    if (choice !== target) {
      flash(choice);
      onWrong?.();
      speak?.(`That one is turned around. Look for lowercase ${target} facing forward.`, { interrupt: true });
      return;
    }
    sfx?.("correct");
    setPicked(choice);
    speak?.("Yes. That letter is facing the right way. Now write it.", { interrupt: true });
  };

  const accepted = () => {
    if (round + 1 >= rounds) finish();
    else {
      setRound((n) => n + 1);
      setPicked(null);
    }
  };

  const counter = <RoundCounter done={round} total={rounds} accent={activity.accent} />;

  return (
    <GameFrame center={!picked}>
      {picked && <RoundBar left={<TargetChip glyph={target} caption="Write" accent={activity.accent} />} right={counter} />}
      {!picked ? (
        <Board title={activity.title} symbol={activity.symbol} accent={activity.accent} right={counter} caption="Which one faces the right way?">
          <ChoiceRow gap={20}>
            {choices.map((choice, i) => (
              <motion.div
                key={`${choice}-${round}-${i}`}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 360, damping: 22, delay: reduceMotion ? 0 : i * 0.07 }}
              >
                <ChoiceButton
                  label={choice}
                  ariaLabel={choice === flipped && choice !== target ? `Backwards ${choice}` : `Lowercase ${choice}`}
                  accent={activity.accent}
                  size="calc(var(--tile, 72px) * 1.35)"
                  flipped={choice === flipped && choice !== target}
                  onClick={() => pick(choice)}
                  wrong={isWrong(choice)}
                  reduceMotion={reduceMotion}
                />
              </motion.div>
            ))}
          </ChoiceRow>
        </Board>
      ) : (
        <WritingHandwritingPad
          key={`${target}-${round}`}
          target={target}
          accent={activity.accent}
          showsGuide={false}
          sfx={sfx}
          reduceMotion={reduceMotion}
          onAccepted={accepted}
          onRejected={() => onWrong?.()}
        />
      )}
    </GameFrame>
  );
};

/* ── Mystery Letter ─────────────────────────────────────────────────────── */
export const MysteryLetterGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const letters = activity.timerHint
    ? WRITING_KNOWN_LETTERS[lesson.lessonNumber] || lessonLetters(lesson.lessonNumber)
    : lessonLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const total = Math.min(letters.length, lesson.lessonNumber === 2 ? 8 : letters.length === 9 ? 8 : letters.length);
  const [index, setIndex] = useState(0);
  const [hint, setHint] = useState(false);
  const letter = letters[index];
  const prompt = lessonMysteryPrompt(lesson.lessonNumber, letter);

  useSpokenItem(speak, prompt);
  useEffect(() => {
    setHint(false);
    if (!activity.timerHint) return undefined;
    const timer = setTimeout(() => {
      setHint(true);
      sfx?.("pop", { volume: 0.4 });
      speak?.("Here is a gentle peek. You can keep writing.", { interrupt: true });
    }, 12000);
    return () => clearTimeout(timer);
  }, [activity.timerHint, letter, sfx, speak]);

  const accepted = () => {
    if (index + 1 >= total) finish();
    else setIndex((n) => n + 1);
  };

  return (
    <GameFrame>
      <RoundBar
        left={
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <IconButton label="Hear the clue again" onClick={() => speak?.(prompt, { interrupt: true })} accent={activity.accent} filled>
              <SpeakerIcon size={20} />
            </IconButton>
            <span style={{ fontWeight: 700, fontSize: 17, color: LEARN.color.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prompt}</span>
          </div>
        }
        right={<RoundCounter done={index} total={total} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={letter}
        target={letter}
        accent={activity.accent}
        showsGuide={false}
        hintForced={hint}
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => {
          onWrong?.();
          speak?.("Not quite yet. Listen once more and try again.", { interrupt: true });
        }}
      />
    </GameFrame>
  );
};

/* ── Fast Letters ───────────────────────────────────────────────────────── */
export const FastLettersGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion, profile }) => {
  const letters = activity.knownPool
    ? WRITING_KNOWN_LETTERS[lesson.lessonNumber] || lessonLetters(lesson.lessonNumber)
    : lessonLetters(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const storeKey = `pixley.learn.writing.lesson${lesson.lessonNumber}.fastLetters.best`;
  const [written, setWritten] = useState([]);
  const [best] = useState(() => writingLocalStore.lastScore(storeKey, profile?.id));
  const writtenRef = useRef(written);
  writtenRef.current = written;

  const seconds = useCountdown(activity.seconds || 60, {
    onEnd: () => {
      writingLocalStore.recordBestScore(storeKey, profile?.id, writtenRef.current.length);
      finish();
    },
  });

  return (
    <GameFrame>
      <RoundBar
        left={<TimerPill seconds={seconds} accent={activity.accent} />}
        right={<ScorePill score={written.length} unit={written.length === 1 ? "letter" : "letters"} best={best} accent={activity.accent} />}
      />
      <CollectedStrip items={written} accent={activity.accent} reduceMotion={reduceMotion} empty="Write any letter you know" />
      <WritingHandwritingPad
        key={written.length}
        target={letters[written.length % letters.length]}
        accent={activity.accent}
        showsGuide={false}
        hintAvailable={false}
        autoSubmit
        letters={letters}
        submitTitle="Check"
        sfx={sfx}
        reduceMotion={reduceMotion}
        acceptDelay={reduceMotion ? 100 : 480}
        onAccepted={(matched) => {
          speak?.(`Lowercase ${matched}.`, { interrupt: true });
          setWritten((prev) => [...prev, matched]);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Word in a Sentence ─────────────────────────────────────────────────── */
export const WordInSentenceGame = ({ lesson, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const sentences = lessonSentences(lesson.lessonNumber);
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [filled, setFilled] = useState(false);
  const item = sentences[index];
  const line = item ? `${item.stretchedNarration} Write ${item.word}.` : "";

  useSpokenItem(speak, line);
  useEffect(() => {
    setFilled(false);
  }, [item]);

  if (!item) return null;

  const accepted = () => {
    setFilled(true);
    speak?.(`You wrote ${item.word}.`, { interrupt: true });
    window.setTimeout(() => {
      if (index + 1 >= sentences.length) finish();
      else setIndex((n) => n + 1);
    }, reduceMotion ? 150 : 700);
  };

  return (
    <GameFrame>
      <RoundBar
        left={
          <SentenceFrame before={`${item.sentenceStart} `} after={` ${item.sentenceEnd}`} filled={filled ? item.word : ""} accent={activity.accent} style={{ flex: 1 }}>
            <WordPicture name={item.assetName} alt={item.word} size="calc(var(--tile, 72px) * 0.95)" onClick={() => speak?.(line, { interrupt: true })} ariaLabel={`Hear ${item.word}`} accent={activity.accent} reduceMotion={reduceMotion} />
            <IconButton label="Hear the word stretched" onClick={() => speak?.(line, { interrupt: true })} accent={activity.accent} filled>
              <SpeakerIcon size={20} />
            </IconButton>
          </SentenceFrame>
        }
        right={<RoundCounter done={index} total={sentences.length} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={item.word}
        target={item.word}
        accent={activity.accent}
        showsGuide={false}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={accepted}
        onRejected={() => {
          onWrong?.();
          speak?.(`Stretch it again. ${item.stretchedNarration}`, { interrupt: true });
        }}
      />
    </GameFrame>
  );
};
